import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowUpRight,
  Briefcase,
  Building2,
  ChevronDown,
  Download,
  FlaskConical,
  Globe,
  Home,
  MoreHorizontal,
  Package,
  Rocket,
  Satellite,
  Settings,
  Share2,
  Shield,
  User,
  Wifi,
  type LucideIcon,
} from 'lucide-react';
import {
  formatUsdMap,
  matchProductGraph,
  type ProductGlyph,
  type ProductLine,
  type ProductRevenueModel,
} from '@/components/value-chain/productRevenue';
import type { ValueChainGraph } from '@/types/valueChain';

type DisplayMode = 'revenue' | 'share';

type Props = {
  model: ProductRevenueModel;
  graphs: ValueChainGraph[];
  onOpenLine: (line: ProductLine, graph: ValueChainGraph | null) => void;
};

const GLYPH: Record<ProductGlyph, LucideIcon> = {
  wifi: Wifi,
  rocket: Rocket,
  satellite: Satellite,
  astronaut: User,
  flask: FlaskConical,
  ellipsis: MoreHorizontal,
  home: Home,
  buildings: Building2,
  package: Package,
  briefcase: Briefcase,
  shield: Shield,
  gear: Settings,
  globe: Globe,
  nasa: Satellite,
};

export function ProductRevenueMapView({ model, graphs, onOpenLine }: Props) {
  const [mode, setMode] = useState<DisplayMode>('revenue');
  const [copied, setCopied] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);

  const handleOpen = (line: ProductLine) => {
    onOpenLine(line, matchProductGraph(graphs, line));
  };

  const handleExport = () => {
    const rows = [
      ['line', 'parent', 'revenue_usd', 'share_pct', 'growth_pct'],
      ...model.lines.flatMap((line) => [
        [line.label, '', String(Math.round(line.revenue)), String(line.sharePct), String(line.growthPct)],
        ...(line.children ?? []).map((child) => [
          child.label,
          line.label,
          String(Math.round(child.revenue)),
          String(child.sharePct),
          String(child.growthPct),
        ]),
      ]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${model.ticker.toLowerCase()}-product-revenue-map.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    const lines = [
      `${model.company} Revenue Map`,
      `Estimated TTM revenue: ${formatUsdMap(model.totalRevenue)}`,
      ...model.lines.map((line) => `${line.label}: ${formatUsdMap(line.revenue)} (${line.sharePct}%)`),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const chartLines = useMemo(
    () =>
      model.lines.map((line) => ({
        name: line.label,
        short: shortLabel(line.label),
        revenueB: line.revenue / 1_000_000_000,
        share: line.sharePct,
        growth: line.growthPct,
        color: line.accent || line.color,
      })),
    [model.lines],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f7f8fa]">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[26px] leading-tight font-semibold tracking-tight text-slate-900">
              {model.company} Revenue Map
            </h2>
            <p className="mt-0.5 text-[13px] text-slate-500">
              Estimated revenue by business line and key subsegments (TTM, USD)
            </p>
          </div>
          <div className="flex items-center gap-3">
            {model.ticker === 'SPCX' ? <SpaceXMark /> : null}
            <div className="relative">
              <button
                type="button"
                onClick={() => setPeriodOpen((open) => !open)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700"
              >
                {model.periodLabel}
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
              {periodOpen ? (
                <div className="absolute right-0 z-20 mt-1 w-44 rounded-xl border border-slate-200 bg-white py-1 shadow-sm">
                  <span className="block px-3 py-2 text-[12px] font-medium text-slate-800">
                    {model.periodLabel}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_260px]">
          <div
            className={`min-h-[420px] ${
              model.layout === 'spacex'
                ? 'grid gap-2'
                : 'flex min-h-[360px] flex-wrap content-stretch gap-2'
            }`}
            style={
              model.layout === 'spacex'
                ? {
                    gridTemplateColumns: 'minmax(0, 2.45fr) minmax(0, 1.65fr) minmax(0, 1.05fr)',
                    gridTemplateRows: 'minmax(168px, 1.25fr) minmax(120px, 0.9fr) minmax(84px, 0.38fr)',
                    gridTemplateAreas: `
                      "starlink launch space-systems"
                      "starlink launch human-spaceflight"
                      "starship other human-spaceflight"
                    `,
                  }
                : undefined
            }
          >
            {model.lines.map((line) => (
              <LineTile
                key={line.id}
                line={line}
                mode={mode}
                layout={model.layout}
                onOpen={handleOpen}
              />
            ))}
          </div>

          <aside className="flex min-h-0 flex-col gap-3">
            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[12px] font-medium text-slate-500">Total Revenue (TTM)</p>
              <div className="mt-1 flex items-end gap-2">
                <p className="text-[32px] leading-none font-semibold tracking-tight text-slate-900">
                  {formatUsdMap(model.totalRevenue)}
                </p>
                <span
                  className={`mb-0.5 inline-flex items-center text-[13px] font-semibold ${
                    model.totalGrowthPct >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  {model.totalGrowthPct > 0 ? '+' : ''}
                  {model.totalGrowthPct}%
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">vs. prior year</p>
              <dl className="mt-4 space-y-2 text-[12px]">
                <MetaRow label="Gross Margin (est.)" value={model.grossMarginPct == null ? '—' : `~${model.grossMarginPct}%`} />
                <MetaRow
                  label="Employees"
                  value={model.employees == null ? '—' : `~${model.employees.toLocaleString()}`}
                />
                <MetaRow label="Headquarters" value={model.headquarters ?? '—'} />
                <MetaRow label="Fiscal Period" value={model.periodLabel.replace(/[()]/g, '')} />
              </dl>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Key Takeaways</h3>
              <ol className="mt-3 space-y-2.5">
                {model.takeaways.map((insight, i) => (
                  <li key={insight} className="flex gap-2.5 text-[12px] leading-relaxed text-slate-600">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-medium text-white">
                      {i + 1}
                    </span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ol>
            </section>
          </aside>
        </div>

        <div className="mt-3 grid gap-3 lg:grid-cols-3">
          <ChartCard title="Revenue by Business Line (USD B)">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartLines} layout="vertical" barSize={12} margin={{ top: 4, right: 36, left: 8, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="short"
                  width={108}
                  tick={{ fontSize: 11, fill: '#475569' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [`$${Number(value).toFixed(1)}B`, 'Revenue']}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="revenueB" radius={[0, 4, 4, 0]}>
                  {chartLines.map((row) => (
                    <Cell key={row.name} fill={row.color} />
                  ))}
                  <LabelList
                    dataKey="revenueB"
                    position="right"
                    formatter={(value: unknown) => `$${Number(value).toFixed(1)}B`}
                    style={{ fontSize: 11, fill: '#64748b' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Revenue Mix">
            <div className="flex h-full items-center gap-2">
              <div className="relative h-full min-w-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartLines}
                      dataKey="share"
                      nameKey="short"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={1.5}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {chartLines.map((row) => (
                        <Cell key={row.name} fill={row.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value}%`, String(name)]}
                      contentStyle={tooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[15px] font-semibold text-slate-900">
                    {formatUsdMap(model.totalRevenue)}
                  </span>
                  <span className="text-[10px] tracking-wide text-slate-400 uppercase">TTM</span>
                </div>
              </div>
              <ul className="w-[118px] shrink-0 space-y-1">
                {chartLines.map((row) => (
                  <li key={row.name} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span className="h-2 w-2 rounded-sm" style={{ background: row.color }} />
                    <span className="min-w-0 flex-1 truncate">{row.short}</span>
                    <span className="tabular-nums">{row.share}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </ChartCard>

          <ChartCard title="YoY Growth by Business Line">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartLines} layout="vertical" barSize={12} margin={{ top: 4, right: 44, left: 8, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="short"
                  width={108}
                  tick={{ fontSize: 11, fill: '#475569' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [`${Number(value) > 0 ? '+' : ''}${value}%`, 'YoY']}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="growth" radius={[0, 4, 4, 0]}>
                  {chartLines.map((row) => (
                    <Cell key={row.name} fill={row.growth >= 0 ? '#22c55e' : '#f43f5e'} />
                  ))}
                  <LabelList
                    dataKey="growth"
                    position="right"
                    formatter={(value: unknown) => `${Number(value) > 0 ? '+' : ''}${value}%`}
                    style={{ fontSize: 11, fill: '#64748b' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className="text-[11px] text-slate-400">
            Note: Figures are estimates based on public filings, company disclosures, and industry data. Not financial
            advice.
          </p>
          <div className="flex items-center gap-2 text-[12px] text-slate-600">
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-white"
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </button>
            <button
              type="button"
              onClick={() => void handleShare()}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-white"
            >
              <Share2 className="h-3.5 w-3.5" />
              {copied ? 'Copied' : 'Share'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white/80 px-6 py-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
          <button
            type="button"
            onClick={() => setMode('revenue')}
            className={`rounded-md px-2.5 py-1 font-medium ${
              mode === 'revenue' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            $ Revenue
          </button>
          <button
            type="button"
            onClick={() => setMode('share')}
            className={`rounded-md px-2.5 py-1 font-medium ${
              mode === 'share' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            % Share
          </button>
        </div>
        <p className="text-[11px] text-slate-400">Data as of {model.asOfLabel}</p>
      </div>
    </div>
  );
}

function LineTile({
  line,
  mode,
  layout,
  onOpen,
}: {
  line: ProductLine;
  mode: DisplayMode;
  layout: ProductRevenueModel['layout'];
  onOpen: (line: ProductLine) => void;
}) {
  const Icon = GLYPH[line.glyph];
  const children = line.children ?? [];
  const compact = layout === 'spacex' && (line.id === 'starship' || line.id === 'other');
  const hero = layout === 'spacex' && ['starlink', 'launch', 'space-systems', 'human-spaceflight'].includes(line.id);

  return (
    <button
      type="button"
      onClick={() => onOpen(line)}
      className={`group flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl text-left text-white shadow-sm transition hover:brightness-105 ${
        layout === 'treemap' ? 'min-h-[160px]' : ''
      }`}
      style={{
        background: line.color,
        gridArea: layout === 'spacex' ? line.id : undefined,
        flex: layout === 'treemap' ? Math.max(line.revenue, 1) : undefined,
        minWidth: layout === 'treemap' ? '180px' : undefined,
      }}
    >
      <div className={`relative min-h-0 flex-1 ${compact ? 'flex items-center justify-between px-4 py-3' : 'p-4'}`}>
        <div className="relative z-10 min-w-0">
          <p className={`font-medium ${compact ? 'text-[13px]' : 'text-[15px]'}`}>{line.label}</p>
          <p className={`font-semibold tracking-tight ${compact ? 'text-[22px] leading-tight' : 'text-[34px] leading-none'}`}>
            {mode === 'share' ? `${line.sharePct}%` : formatUsdMap(line.revenue)}
          </p>
          <p className={`opacity-90 ${compact ? 'text-[11px]' : 'mt-1 text-[12px]'}`}>
            {mode === 'share' ? formatUsdMap(line.revenue) : `${line.sharePct}% of total`}
          </p>
        </div>
        <Icon
          className={`absolute text-white/35 ${compact ? 'top-3 right-3 h-5 w-5' : 'top-4 right-4 h-6 w-6'}`}
        />
        {hero ? <HeroArt kind={line.id} /> : null}
      </div>
      {children.length ? (
        <div className="flex gap-1 p-1">
          {children.map((child) => (
            <SubTile key={child.id} line={child} mode={mode} onOpen={onOpen} />
          ))}
        </div>
      ) : null}
    </button>
  );
}

function SubTile({
  line,
  mode,
  onOpen,
}: {
  line: ProductLine;
  mode: DisplayMode;
  onOpen: (line: ProductLine) => void;
}) {
  const Icon = GLYPH[line.glyph];
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(line);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          onOpen(line);
        }
      }}
      className="min-w-0 flex-1 rounded-xl p-2.5 text-left"
      style={{ background: line.accent, flex: Math.max(line.revenue, 1) }}
    >
      <span className="flex items-start justify-between gap-1">
        <span className="line-clamp-2 text-[11px] leading-tight font-medium">{line.label}</span>
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-80" />
      </span>
      <span className="mt-1 block text-[16px] leading-none font-semibold">
        {mode === 'share' ? `${line.sharePct}%` : formatUsdMap(line.revenue)}
      </span>
      <span className="mt-0.5 block text-[10px] opacity-80">
        {mode === 'share' ? formatUsdMap(line.revenue) : `${line.sharePct}%`}
      </span>
    </span>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="relative h-[220px] overflow-hidden rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-1 text-[13px] font-semibold text-slate-800">{title}</h3>
      <div className="h-[170px]">{children}</div>
    </section>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}

function HeroArt({ kind }: { kind: string }) {
  if (kind === 'starlink') {
    return (
      <svg viewBox="0 0 220 140" className="pointer-events-none absolute right-2 bottom-1 h-[72%] w-auto opacity-90">
        <defs>
          <linearGradient id="dish" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.85" />
          </linearGradient>
        </defs>
        <ellipse cx="118" cy="78" rx="78" ry="18" fill="#0f172a" opacity="0.12" />
        <path d="M48 86c22-46 96-46 124 4-28 10-92 10-124-4Z" fill="url(#dish)" />
        <path d="M70 58c18-8 62-8 86 6" fill="none" stroke="#fff" strokeWidth="3" opacity="0.7" />
        <rect x="112" y="84" width="10" height="28" rx="2" fill="#e2e8f0" />
        <path d="M96 112h44l-6 10H102Z" fill="#cbd5e1" />
        <circle cx="168" cy="28" r="7" fill="#fff" opacity="0.85" />
      </svg>
    );
  }
  if (kind === 'launch') {
    return (
      <svg viewBox="0 0 160 160" className="pointer-events-none absolute right-3 bottom-2 h-[78%] w-auto opacity-90">
        <path d="M80 12c18 28 18 64 8 96H72C62 76 62 40 80 12Z" fill="#fff" opacity="0.92" />
        <path d="M72 108h16l8 20H64Z" fill="#fdba74" />
        <path d="M64 128h32s-6 22-16 28c-10-6-16-28-16-28Z" fill="#fb923c" />
        <circle cx="80" cy="48" r="6" fill="#7dd3fc" />
        <path d="M58 86l-16 18 18-6" fill="#fecaca" />
        <path d="M102 86l16 18-18-6" fill="#fecaca" />
      </svg>
    );
  }
  if (kind === 'space-systems') {
    return (
      <svg viewBox="0 0 140 100" className="pointer-events-none absolute right-3 top-10 h-16 w-auto opacity-90">
        <rect x="58" y="28" width="24" height="44" rx="8" fill="#fff" />
        <rect x="18" y="44" width="104" height="10" rx="2" fill="#dcfce7" />
        <circle cx="70" cy="50" r="8" fill="#86efac" />
        <rect x="4" y="46" width="16" height="6" rx="1" fill="#fff" />
        <rect x="120" y="46" width="16" height="6" rx="1" fill="#fff" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 90 110" className="pointer-events-none absolute right-4 bottom-3 h-[58%] w-auto opacity-90">
      <circle cx="46" cy="22" r="12" fill="#fff" />
      <path d="M28 40c0-10 36-10 36 4v28c0 14-8 22-18 22s-18-8-18-22Z" fill="#ede9fe" />
      <path d="M32 70c10 8 28 8 36 0" fill="none" stroke="#c4b5fd" strokeWidth="4" />
    </svg>
  );
}

function SpaceXMark() {
  return (
    <span className="hidden items-center pr-1 text-[18px] font-semibold tracking-[0.18em] text-slate-900 sm:inline-flex">
      SPACE<span className="relative -mx-0.5 inline-block -skew-x-12 text-[20px]">X</span>
    </span>
  );
}

function shortLabel(label: string): string {
  if (label.length <= 18) return label;
  return label.replace(' & Other', '').replace(' Services', '').replace(' & Development', '');
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 10,
  border: '1px solid #e2e8f0',
};

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}
